/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Dimension, orderedDimensions, ORDERED_IMPACTS_BY_DIMENSION } from '@upright/common/model/types';
import {
  CarouselLayout,
  LensBoxMode,
  Side,
} from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { getDimensionIndex, toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { first, last, range, sum } from 'lodash';

/* eslint-disable @typescript-eslint/no-shadow */

interface BaseLayoutProps {
  width: number;
  boxesAreaWidth?: (width: number) => number | undefined;
  maxDataAreaWidth?: (width: number) => number;
  centerAreaWidth?: (width: number) => number | undefined;
  aggregateResultAreaHeight: number | undefined;
  showTotalSums: boolean | undefined;
  boxMode?: LensBoxMode;
  hideTopDescriptionsArea: boolean;
  hasAdditionalInfoArea: boolean;
}

export function baseLayout(props: BaseLayoutProps): CarouselLayout {
  const { width } = props;

  const maxDataAreaWidth = props.maxDataAreaWidth ? props.maxDataAreaWidth(width) : 300;
  const boxMode = props.boxMode || LensBoxMode.SIDE;
  const boxesWidth =
    props.boxesAreaWidth && props.boxesAreaWidth(width) !== undefined ? props.boxesAreaWidth(width)! : 200;
  const boxesAreaWidth = boxMode ? 0 : boxesWidth;
  const centerAreaWidth =
    props.centerAreaWidth && props.centerAreaWidth(width) !== undefined ? props.centerAreaWidth(width)! : 170;
  const aggregateResultAreaHeight = props.aggregateResultAreaHeight !== undefined ? props.aggregateResultAreaHeight : 0;

  function getLineSpaceAreaWidth() {
    if (boxMode === LensBoxMode.MODAL) {
      return 0;
    }
    const availableWidth = width / 2 - centerAreaWidth - boxesAreaWidth;
    const proportion = 0.15;
    return Math.max(10, proportion * availableWidth);
  }

  const lineSpaceAreaWidth = getLineSpaceAreaWidth();
  const dimensionAggregateAreaHeight = 30 * 0.9;
  const impactAreaHeight = 20;
  const topPadding = 0;
  const dimensionImpactsMargin = 5;
  const sideMargin = getSideMargin();
  const totalSumsAreaHeight = props.showTotalSums ? dimensionAggregateAreaHeight : 0;
  const totalSumsDataAreaWidth = 60;
  const additionalInfoHeight = props.hasAdditionalInfoArea ? 60 : 0;

  function getSideMargin() {
    if (width > 700) {
      return 20;
    }
    return 5;
  }

  function topDescriptionsArea() {
    const narrowMode = width < 600;
    const height = (() => {
      if (props.hideTopDescriptionsArea) return 0;
      if (narrowMode) return 22 * 2;
      return 35;
    })();
    const y1 = topPadding;
    const y2 = y1 + height;
    const x2 = width - sideMargin;
    return { narrowMode, ...toBounds({ x1: sideMargin, x2, y1, y2 }) };
  }

  function topTitlesArea() {
    const tda = topDescriptionsArea();
    const topMargin = 10;
    const height = 25;
    const y1 = tda.y2 + topMargin;
    const y2 = y1 + height;
    const x2 = width - sideMargin;
    return toBounds({ x1: sideMargin, x2, y1, y2 });
  }

  function dimensionArea(dimension: Dimension) {
    const dimensionIndex = getDimensionIndex(dimension);
    const y1 =
      topTitlesArea().y2 +
      sum(
        range(0, dimensionIndex).map(index => {
          const currentDimension = orderedDimensions[index];
          const impacts = ORDERED_IMPACTS_BY_DIMENSION[currentDimension];
          const dimensionImpactCount = impacts.length;
          return dimensionImpactCount * impactAreaHeight + dimensionAggregateAreaHeight + dimensionImpactsMargin * 2;
        }),
      );
    const impactCount = ORDERED_IMPACTS_BY_DIMENSION[dimension].length;
    const height = impactCount * impactAreaHeight;
    const y2 = y1 + height + dimensionAggregateAreaHeight + dimensionImpactsMargin * 2;
    return toBounds({
      y1,
      y2,
      x1: sideMargin,
      x2: width - sideMargin,
    });
  }

  function dimensionHeadArea(dimension: Dimension) {
    const da = dimensionArea(dimension);
    return toBounds({
      x1: da.x1,
      x2: da.x2,
      y1: da.y1,
      y2: da.y1 + dimensionAggregateAreaHeight,
    });
  }

  function impactArea(impact: ImpactCategoryId) {
    const dimension = toDimension(impact);
    const daa = dimensionHeadArea(dimension);
    const impacts = ORDERED_IMPACTS_BY_DIMENSION[dimension];
    const impactIndex = impacts.indexOf(impact);
    const y1 = daa.y2 + impactIndex * impactAreaHeight + dimensionImpactsMargin;
    return toBounds({
      x1: daa.x1,
      x2: daa.x2,
      y1,
      y2: y1 + impactAreaHeight,
    });
  }

  function dimensionHeadCenterArea(dimension: Dimension) {
    const da = dimensionHeadArea(dimension);
    const x1 = width / 2 - centerAreaWidth / 2;
    const x2 = x1 + centerAreaWidth;
    return toBounds({
      x1,
      x2,
      y1: da.y1,
      y2: da.y2,
    });
  }

  function dimensionCenterArea(dimension: Dimension) {
    const da = dimensionArea(dimension);
    const x1 = width / 2 - centerAreaWidth / 2;
    const x2 = x1 + centerAreaWidth;
    return toBounds({
      x1,
      x2,
      y1: da.y1,
      y2: da.y2,
    });
  }

  function impactCenterArea(impact: ImpactCategoryId) {
    const ia = impactArea(impact);
    const x1 = width / 2 - centerAreaWidth / 2;
    const x2 = x1 + centerAreaWidth;
    return toBounds({
      x1,
      x2,
      y1: ia.y1,
      y2: ia.y2,
    });
  }

  function centerArea() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstDa = dimensionCenterArea(first(orderedDimensions)!);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const lastDa = dimensionCenterArea(last(orderedDimensions)!);
    return toBounds({
      x1: firstDa.x1,
      x2: firstDa.x2,
      y1: firstDa.y1,
      y2: lastDa.y2,
    });
  }

  /**
   * The x-coordinates for the content area (outer edges of the box area)
   */
  function getContentAreaX() {
    const ca = centerArea();
    const x2 = Math.min(ca.x2 + maxDataAreaWidth + lineSpaceAreaWidth + boxesAreaWidth, width - sideMargin);
    const x1 = Math.max(sideMargin, ca.x1 - maxDataAreaWidth - lineSpaceAreaWidth - boxesAreaWidth);
    return { x1, x2 };
  }

  function totalSumsTitleArea() {
    const ca = centerArea();
    const y1 = ca.y2;
    const y2 = y1 + totalSumsAreaHeight;
    const x1 = ca.x1;
    const x2 = ca.x2;

    return toBounds({
      y1,
      y2,
      x1,
      x2,
    });
  }

  function totalSumsDataArea(side: Side) {
    const titleArea = totalSumsTitleArea();
    const y1 = titleArea.y1;
    const y2 = titleArea.y2;

    if (side === Side.RIGHT) {
      return toBounds({
        x1: titleArea.x2,
        x2: titleArea.x2 + totalSumsDataAreaWidth,
        y1,
        y2,
      });
    } else {
      return toBounds({
        x1: titleArea.x1 - totalSumsDataAreaWidth,
        x2: titleArea.x1,
        y1,
        y2,
      });
    }
  }

  /**
   * For additional information such as model release and legend (on desktop bottom left and right)
   */
  function additionalInfoArea(side: Side) {
    const cax = getContentAreaX();
    const y2 = totalSumsTitleArea().y2;
    const y1 = y2 - additionalInfoHeight;

    if (side === Side.RIGHT) {
      return toBounds({
        x1: cax.x2 - boxesAreaWidth - lineSpaceAreaWidth,
        x2: cax.x2,
        y1,
        y2,
      });
    }
    return toBounds({
      x1: cax.x1,
      x2: cax.x1 + boxesAreaWidth + lineSpaceAreaWidth,
      y1,
      y2,
    });
  }

  function boxesArea(side: Side) {
    const ca = centerArea();
    const y1 = ca.y1;
    const cax = getContentAreaX();
    const aia = additionalInfoArea(side);
    const y2 = aia.y1;

    if (side === Side.RIGHT) {
      const x2 = cax.x2;
      const x1 = x2 - boxesAreaWidth;
      return toBounds({ x1, x2, y1, y2 });
    } else {
      const x1 = cax.x1;
      const x2 = x1 + boxesAreaWidth;
      return toBounds({ x1, x2, y1, y2 });
    }
  }

  function lineSpaceArea(side: Side) {
    const ba = boxesArea(side);
    const y1 = ba.y1;
    const y2 = ba.y2;

    if (side === Side.RIGHT) {
      const x2 = ba.x1;
      const x1 = x2 - lineSpaceAreaWidth;
      return toBounds({ x1, x2, y1, y2 });
    } else {
      const x1 = ba.x2;
      const x2 = x1 + lineSpaceAreaWidth;
      return toBounds({ x1, x2, y1, y2 });
    }
  }

  function dataArea(side: Side) {
    const ca = centerArea();
    const ba = lineSpaceArea(side);
    const y1 = ca.y1;
    const y2 = ca.y2;
    if (side === Side.RIGHT) {
      return toBounds({
        x1: ca.x2,
        x2: ba.x1,
        y1,
        y2,
      });
    } else {
      return toBounds({
        x1: ba.x2,
        x2: ca.x1,
        y1,
        y2,
      });
    }
  }

  function dimensionHeadDataArea(side: Side, dimension: Dimension) {
    const da = dataArea(side);
    const daa = dimensionHeadArea(dimension);
    return toBounds({
      y1: daa.y1,
      y2: daa.y2,
      x1: da.x1,
      x2: da.x2,
    });
  }

  /**
   * Area for data on a single impact
   */
  function impactDataArea(side: Side, impact: ImpactCategoryId) {
    const da = dataArea(side);
    const ia = impactArea(impact);
    const ret = toBounds({
      y1: ia.y1,
      y2: ia.y2,
      x1: da.x1,
      x2: da.x2,
    });
    return ret;
  }

  /**
   * The aggregate result area (in the bottom on desktop)
   */
  function aggregateResultArea() {
    const ta = totalSumsTitleArea();
    const y1 = ta.y2;
    const y2 = y1 + aggregateResultAreaHeight;
    const sideMargin = getSideMargin();

    const dal = boxesArea(Side.LEFT);
    const dar = boxesArea(Side.RIGHT);

    return toBounds({
      y1: y1,
      y2: y2,
      x1: dal.x1 - sideMargin,
      x2: dar.x2 + sideMargin,
    });
  }

  function fullArea() {
    const ag = aggregateResultArea();
    const x1 = 0;
    const x2 = width;
    const y1 = 0;
    const y2 = ag.y2;
    return toBounds({ x1, x2, y1, y2 });
  }

  return {
    topDescriptionsArea,
    topTitlesArea,
    dimensionArea,
    dimensionHeadArea,
    dimensionHeadCenterArea,
    dimensionCenterArea,
    dimensionHeadDataArea,
    impactArea,
    impactCenterArea,
    impactDataArea,
    centerArea,
    lineSpaceArea,
    boxesArea,
    dataArea,
    aggregateResultArea,
    fullArea,
    boxMode,
    boxesWidth,
    totalSumsDataArea,
    totalSumsTitleArea,
    additionalInfoArea,
  };
}

// temporarily here as importing this causes some circular dependency problem
export function toDimension(impactCode: string): Dimension {
  switch (impactCode.slice(0, 1)) {
    case 'E':
      return Dimension.ENVIRONMENT;
    case 'K':
      return Dimension.KNOWLEDGE;
    case 'S':
      return Dimension.SOCIAL;
    case 'H':
      return Dimension.HEALTH;
  }
  throw Error(`Unknown dimension for: ${impactCode}`);
}
